import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import MeetHeroCard from '../MeetHeroCard';
import '../../styles/MeetHeroCard.css';

// Define the Hero interface
interface Hero {
  id: number;
  name: string;
  title: string;
  image: string;
  linkedin: string;
  course: string;
}

// Move heroes data outside to prevent recreation on each render
const HEROES: Hero[] = [
  {
    id: 1,
    name: 'Laila Samat',
    title: 'Fashion Business Trainer',
    image: 'images/fashion.webp',
    linkedin: '#',
    course: 'Fashion Business',
  },
  {
    id: 2,
    name: 'Asmae Aboubigi',
    title: 'E-commerce Expert',
    image: 'images/asmae.webp',
    linkedin: 'https://www.linkedin.com/in/asmae-aboubigi/',
    course: 'E-commerce Mastery',
  },
  {
    id: 3,
    name: 'Zaroui Imane',
    title: 'UGC Creation Specialist',
    image: 'images/imane.webp',
    linkedin: '#',
    course: 'UGC Creation',
  },
];

interface State {
  currentIndex: number;
  isDragging: boolean;
  startX?: number;
  scrollLeft?: number;
}

const MeetHeroes: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({
    currentIndex: 0,
    isDragging: false
  });

  // Calculate isMobile once on mount and when window size changes
  const [isMobile, setIsMobile] = useState(false);
  
  // Handle window resize using a resize observer instead of event listeners
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check initially
    checkMobile();
    
    // Set up observer
    const resizeObserver = new ResizeObserver(() => {
      checkMobile();
    });
    
    resizeObserver.observe(document.body);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Use IntersectionObserver to detect when component is in view
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true // Only trigger once for better performance
  });

  // Optimized scroll handler using requestAnimationFrame
  const scrollToIndex = useCallback((index: number) => {
    if (!scrollRef.current) return;
    
    const cardWidth = isMobile ? 300 : scrollRef.current.offsetWidth / HEROES.length;
    const scrollTo = index * cardWidth;
    
    // Use requestAnimationFrame for smoother scrolling
    window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          left: scrollTo,
          behavior: 'smooth'
        });
      }
    });
  }, [isMobile]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    setState(prev => ({ 
      ...prev, 
      isDragging: true, 
      startX: touch.clientX, 
      scrollLeft: scrollRef.current?.scrollLeft || 0 
    }));
  }, [isMobile]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const { isDragging, startX, scrollLeft } = state;
    if (!isDragging || !scrollRef.current || !isMobile || typeof startX !== 'number' || typeof scrollLeft !== 'number') return;
    e.preventDefault();
    
    const x = e.touches[0].clientX;
    const walk = (startX - x) * 2;
    
    // Use requestAnimationFrame for smoother scrolling
    window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollLeft + walk;
      }
    });
  }, [state, isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile || !scrollRef.current) return;
    
    // Calculate which card is closest to the viewport
    const currentScroll = scrollRef.current.scrollLeft;
    const cardWidth = 300;
    const newIndex = Math.round(currentScroll / cardWidth);
    
    // Update state and scroll to the closest card
    setState(prev => ({ 
      ...prev, 
      isDragging: false,
      currentIndex: Math.max(0, Math.min(newIndex, HEROES.length - 1)) 
    }));
    
    scrollToIndex(newIndex);
  }, [isMobile, scrollToIndex]);

  return (
    <section ref={ref} className="bg-white py-20 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-100 opacity-50" />
      <div className="relative max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">
            Meet Our Trainers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert tutors dedicated to transforming lives through knowledge and inspiration.
          </p>
        </div>

        {isMobile ? (
          <div 
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex gap-6 px-4">
              {HEROES.map((hero) => (
                <div
                  key={hero.id}
                  className="flex-shrink-0"
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <MeetHeroCard {...hero} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="hidden md:flex md:justify-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
              {HEROES.map((hero) => (
                <div key={hero.id} className="transform transition-all duration-500">
                  <MeetHeroCard {...hero} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Only render dots for mobile view */}
        {isMobile && (
          <div className="flex justify-center gap-2 mt-6">
            {HEROES.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  state.currentIndex === index ? 'bg-primary w-4' : 'bg-gray-300'
                }`}
                onClick={() => {
                  setState(prev => ({ ...prev, currentIndex: index }));
                  scrollToIndex(index);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default React.memo(MeetHeroes);
